import React, { useState, useEffect, useRef, useCallback } from 'react';
// Google認証用の関数
import { GoogleAuthProvider, signInWithPopup, signOut, signInAnonymously } from 'firebase/auth';
import { Play, Pause, SkipBack, SkipForward, Volume2, Music, Home, Sparkles, Waves, Moon, Zap, Coffee, ChevronLeft, ChevronRight, Menu, X, Disc, AlertTriangle, FileText, PlusSquare, Upload, Image as ImageIcon, Lock, LogIn, LogOut, Chrome, Download, Smartphone } from 'lucide-react';

// --- Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyDaGkRiRbe54qV85-32ZS09AALS8KlGrLU",
  authDomain: "mymusicplayer-ef8f0.firebaseapp.com",
  projectId: "mymusicplayer-ef8f0",
  storageBucket: "mymusicplayer-ef8f0.firebasestorage.app",
  messagingSenderId: "305125896450",
  appId: "1:305125896450:web:eb15f3650452fe442f521b",
  measurementId: "G-4M73KXLS97"
};

const APP_ID = 'akiko-music-v1';
const SITE_DOMAIN = 'https://akiko.cosu.jp';

// 【重要】管理者として許可するGoogleメールアドレス
const ADMIN_EMAIL = 'yutario8888@gmail.com'; 

// --- 初期データ ---
const INITIAL_TRACKS = [
  {
    "id": "jOoMnh6xwNSq4LAkDsqB",
    "artist": "Akiko",
    "url": "https://akiko.cosu.jp/music/uchiagehanabi.mp3",
    "genre": "spring",
    "title": "打ち上げ花火",
    "cover": "https://akiko.cosu.jp/images/fireworks.webp",
    "order": "1"
  },
  {
    "id": "qXhRrr5B8tqZ8xQlwxDI",
    "artist": "Akiko",
    "order": "2",
    "cover": "https://akiko.cosu.jp/images/cherry.webp",
    "title": "桜とともに",
    "genre": "spring",
    "url": "https://akiko.cosu.jp/music/Sakura-Melodies.mp3"
  },
  {
    "id": "2cBpZT1EF99IcBcpdSE5",
    "artist": "Akiko",
    "order": "3",
    "title": "MIKAN",
    "cover": "https://akiko.cosu.jp/images/mikan.webp",
    "url": "https://akiko.cosu.jp/music/MIKAN.mp3",
    "genre": "spring"
  }
];

// --- 安全なLocalStorageアクセスのためのヘルパー関数 ---
const getStorageItem = (key, defaultValue) => {
  try {
    if (typeof window !== 'undefined') {
      const item = localStorage.getItem(key);
      return item !== null ? item : defaultValue;
    }
  } catch (e) {
    console.warn(`LocalStorage access denied for ${key}:`, e);
  }
  return defaultValue;
};

const setStorageItem = (key, value) => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.warn(`LocalStorage write failed for ${key}:`, e);
  }
};

export default function App() {
  const [user, setUser] = useState(null);
  const [tracks, setTracks] = useState(INITIAL_TRACKS);
  const [filteredTracks, setFilteredTracks] = useState(INITIAL_TRACKS);
  
  const [currentTrack, setCurrentTrack] = useState(() => {
    const savedTrackId = getStorageItem('akiko_last_track_id', null);
    if (savedTrackId) {
      const found = INITIAL_TRACKS.find(t => t.id === savedTrackId);
      return found || INITIAL_TRACKS[0];
    }
    return INITIAL_TRACKS[0];
  });
    
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const [volume, setVolume] = useState(() => {
    const savedVol = parseFloat(getStorageItem('akiko_volume', '0.7'));
    return !isNaN(savedVol) ? savedVol : 0.7;
  });
    
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [activePlaylist, setActivePlaylist] = useState('spring');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [generatingSitemap, setGeneratingSitemap] = useState(false);

  const tracksRef = useRef(INITIAL_TRACKS);
  const filteredTracksRef = useRef(INITIAL_TRACKS);
  const currentTrackRef = useRef(currentTrack);
  const isPlayingRef = useRef(false);
  const rafRef = useRef(null); 
  
  // ★重要: 誤再生防止のためのクールダウンフラグ
  const pauseCooldownRef = useRef(false);
  // ★重要: バックグラウンド維持のためのWakeLock
  const wakeLockRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [authError, setAuthError] = useState(null);

  const [newTrackData, setNewTrackData] = useState({
    title: '',
    artist: '',
    genre: 'spring',
    audioUrl: '',
    coverUrl: '',
    order: 0
  });
    
  const firebaseRefs = useRef({
    auth: null,
    db: null,
    funcs: null
  });

  const audioRef = useRef(new Audio());
  const scrollContainerRef = useRef(null);
  const trackRefs = useRef({});

  const playlists = [
    { id: 'spring', label: 'spring', icon: <Coffee size={20} aria-hidden="true" />, color: 'from-rose-400 to-pink-600', glow: 'rgba(251, 113, 133, 0.4)', text: 'rose' },
    { id: 'summer', label: 'summer', icon: <Zap size={20} aria-hidden="true" />, color: 'from-yellow-300 to-orange-500', glow: 'rgba(253, 224, 71, 0.4)', text: 'yellow' },
    { id: 'autumn', label: 'autumn', icon: <Sparkles size={20} aria-hidden="true" />, color: 'from-cyan-300 to-blue-500', glow: 'rgba(103, 232, 249, 0.4)', text: 'cyan' },
    { id: 'winter', label: 'winter', icon: <Moon size={20} aria-hidden="true" />, color: 'from-violet-400 to-purple-800', glow: 'rgba(167, 139, 250, 0.4)', text: 'violet' },
    { id: 'event', label: 'event', icon: <Waves size={20} aria-hidden="true" />, color: 'from-emerald-300 to-teal-500', glow: 'rgba(110, 231, 183, 0.4)', text: 'emerald' },
    { id: 'All', label: 'All Songs', icon: <Home size={22} aria-hidden="true" />, color: 'from-fuchsia-400 to-indigo-500', glow: 'rgba(192, 38, 211, 0.4)', text: 'fuchsia' },
  ];

  // --- Sync Refs ---
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { filteredTracksRef.current = filteredTracks; }, [filteredTracks]);
  useEffect(() => { 
      currentTrackRef.current = currentTrack;
      if (currentTrack?.id) setStorageItem('akiko_last_track_id', currentTrack.id);
  }, [currentTrack]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { setStorageItem('akiko_volume', volume); }, [volume]);

  // --- Viewport Fix ---
  useEffect(() => {
    const setAppHeight = () => {
        const doc = document.documentElement;
        const height = window.innerHeight || document.documentElement.clientHeight;
        doc.style.setProperty('--app-height', `${height}px`);
    };
    window.addEventListener('resize', setAppHeight);
    setAppHeight();
    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  // --- 1. SEO & Metadata ---
  useEffect(() => {
    document.title = "AKIKO Music Player";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Stream the best collection of LoFi, Chill, Energy, and Focus music on AKIKO Music Player. Listen to 'MIKAN' and more.";

    const trackListElement = tracks.map((track, index) => ({
      "@type": "MusicRecording",
      "name": track.title,
      "byArtist": { "@type": "MusicGroup", "name": track.artist },
      "url": `${SITE_DOMAIN}/track/${track.id}`, 
      "image": track.cover,
      "duration": "PT3M30S",
      "position": index + 1
    }));

    const structuredData = [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "AKIKO Music Player",
        "url": SITE_DOMAIN,
        "description": "Stream the best collection of LoFi, Chill, Energy, and Focus music.",
        "applicationCategory": "MultimediaApplication",
        "operatingSystem": "Web Browser",
        "author": { "@type": "Person", "name": "AKIKO", "url": SITE_DOMAIN },
        "copyrightHolder": { "@type": "Person", "name": "AKIKO" },
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "JPY" }
      },
      {
        "@context": "https://schema.org",
        "@type": "MusicPlaylist",
        "name": "AKIKO Music Collection",
        "numTracks": tracks.length,
        "track": trackListElement,
        "genre": "Lo-Fi",
        "mainEntityOfPage": { "@type": "WebPage", "@id": SITE_DOMAIN },
        "potentialAction": {
            "@type": "ListenAction",
            "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${SITE_DOMAIN}?play=true`,
            "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
            },
            "expectsAcceptanceOf": { "@type": "Offer", "category": "free" }
        }
      }
    ];

    let script = document.querySelector('#akiko-json-ld-dynamic');
    if (!script) {
      script = document.createElement('script');
      script.id = 'akiko-json-ld-dynamic';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.text = JSON.stringify(structuredData);
  }, [tracks]); 

  // --- 2. Initialize Firebase ---
  useEffect(() => {
    let unsubscribeAuth = null;
    let unsubscribeSnapshot = null;

    const initFirebase = async () => {
      try {
        const [
          { initializeApp },
          { getAuth, onAuthStateChanged, signInAnonymously },
          { getFirestore, collection, onSnapshot, query, addDoc, serverTimestamp, getDocs }
        ] = await Promise.all([
          import('firebase/app'),
          import('firebase/auth'),
          import('firebase/firestore')
        ]);

        const app = initializeApp(firebaseConfig);
        const auth = getAuth(app);
        const db = getFirestore(app);

        firebaseRefs.current = {
          auth,
          db,
          funcs: { collection, getDocs, addDoc, serverTimestamp }
        };

        unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            const tracksRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'tracks');
            unsubscribeSnapshot = onSnapshot(query(tracksRef), (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                data.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

                if (data.length > 0) {
                  setTracks(data);
                  if (!currentTrack || !data.some(t => t.id === currentTrack.id)) {
                      const found = data.find(t => t.id === currentTrackRef.current?.id);
                      if (found) {
                          setCurrentTrack(found); 
                      } else {
                          setCurrentTrack(data[0]); 
                      }
                  }
                }
                setLoading(false);
                setErrorMsg(null);
            }, (error) => {
                console.error("Firestore Error:", error);
            });
        });

      } catch (e) {
        console.error("Firebase init error:", e);
      }
    };

    if (typeof window !== 'undefined') {
        setTimeout(initFirebase, 100); 
    }

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, []);

  // --- Admin Login Logic ---
  const handleGoogleLogin = async () => {
    setAuthError(null);
    try {
        const { auth } = firebaseRefs.current;
        if (!auth) throw new Error("Firebase Auth not initialized");
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
    } catch (error) {
        console.error(error);
        setAuthError(`Google Login failed: ${error.message}`);
    }
  };

  // --- Upload Logic ---
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!user || user.isAnonymous || uploading) return;

    if (user.email !== ADMIN_EMAIL) {
        alert(`Access Denied.\nYour email (${user.email}) is not authorized to upload tracks.`);
        return;
    }
      
    const { title, artist, genre, audioUrl, coverUrl, order } = newTrackData;
    if (!title || !artist || !audioUrl) {
      alert("Please fill in all required fields.");
      return;
    }

    setUploading(true);
    try {
      const { db, funcs } = firebaseRefs.current;
      if (!db) throw new Error("Database not initialized");
      const { collection, addDoc, serverTimestamp } = funcs;

      await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'tracks'), {
        title,
        artist,
        genre,
        url: audioUrl,
        cover: coverUrl,
        order: Number(order) || 0,
        createdAt: serverTimestamp()
      });

      setIsModalOpen(false);
      setNewTrackData({ title: '', artist: '', genre: 'spring', audioUrl: '', coverUrl: '', order: 0 });
      alert("Track added successfully!");

    } catch (error) {
      console.error("Upload failed:", error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // --- Sitemap Logic ---
  const generateSitemap = async () => {
    if (generatingSitemap || !firebaseRefs.current.db) return;
    setGeneratingSitemap(true);
    try {
        const { db, funcs } = firebaseRefs.current;
        const { getDocs, collection } = funcs;
        const baseUrl = SITE_DOMAIN;
        const today = new Date().toISOString().split('T')[0];

        let xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url><loc>${baseUrl}/</loc><lastmod>${today}</lastmod><changefreq>daily</changefreq><priority>1.0</priority></url>`;

        const querySnapshot = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'tracks'));
        const docs = [];
        querySnapshot.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
        docs.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

        docs.forEach((data) => {
            const trackId = data.id;
            const lastMod = data.createdAt ? new Date(data.createdAt.seconds * 1000).toISOString().split('T')[0] : today;
            xml += `
  <url>
    <loc>${baseUrl}/track/${trackId}</loc>
    <lastmod>${lastMod}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
    <image:image xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">
       <image:loc>${data.cover || ''}</image:loc>
       <image:title>${data.title} - ${data.artist}</image:title>
    </image:image>
  </url>`;
        });
        xml += `\n</urlset>`;

        const blob = new Blob([xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sitemap.xml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error("Sitemap failed:", e);
    } finally {
        setGeneratingSitemap(false);
    }
  };

  // --- Export Tracks ---
  const exportTracks = () => {
      const cleanTracks = tracks.map(({ createdAt, ...rest }) => rest);
      console.log("▼▼▼ COPY BELOW ▼▼▼");
      console.log(JSON.stringify(cleanTracks, null, 2));
      console.log("▲▲▲ COPY ABOVE ▲▲▲");
      alert("Tracks data exported to Console (F12). Please copy it.");
  };

  // --- Stable Player Logic ---
  const getPlaylist = useCallback(() => {
    return filteredTracksRef.current.length > 0 ? filteredTracksRef.current : tracksRef.current;
  }, []);

  const playNext = useCallback(() => {
    const list = getPlaylist();
    if (list.length === 0) return;
    const idx = list.findIndex(t => t.id === currentTrackRef.current?.id);
    const nextIndex = (idx + 1) % list.length;
    setCurrentTrack(list[nextIndex]);
    setIsPlaying(true); 
  }, [getPlaylist]);

  const playPrev = useCallback(() => {
    const list = getPlaylist();
    if (list.length === 0) return;
    const idx = list.findIndex(t => t.id === currentTrackRef.current?.id);
    const prevIndex = (idx - 1 + list.length) % list.length;
    setCurrentTrack(list[prevIndex]);
    setIsPlaying(true); 
  }, [getPlaylist]);

  const handleNext = () => playNext();
  const handlePrev = () => playPrev();

  const handleTrackClick = (track) => {
    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current.pause(); 
      } else {
        audioRef.current.play().catch(console.warn);
      }
    } else {
      setCurrentTrack(track);
      setIsPlaying(true);
    }
  };

  // --- SMOOTH PROGRESS BAR ANIMATION (60FPS) ---
  const animateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
      }
      rafRef.current = requestAnimationFrame(animateProgress);
    }
  }, []);

  // --- 4. ROBUST AUDIO & BLUETOOTH HANDLER ---
  const syncPositionState = (audioElement) => {
    if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
        if(audioElement.duration && !isNaN(audioElement.duration) && isFinite(audioElement.duration)) {
            try {
                navigator.mediaSession.setPositionState({
                    duration: audioElement.duration,
                    playbackRate: audioElement.playbackRate,
                    position: audioElement.currentTime
                });
            } catch(e) { /* ignore */ }
        }
    }
  };

  const updateMetadata = useCallback(() => {
    if (!currentTrackRef.current || !('mediaSession' in navigator)) return;
    
    const track = currentTrackRef.current;
    const artworkUrl = track.cover || 'https://via.placeholder.com/512';

    try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: track.title,
          artist: track.artist,
          album: track.album || 'AKIKO Music',
          artwork: [
              { src: artworkUrl, sizes: '96x96', type: 'image/png' },
              { src: artworkUrl, sizes: '128x128', type: 'image/png' },
              { src: artworkUrl, sizes: '192x192', type: 'image/png' },
              { src: artworkUrl, sizes: '256x256', type: 'image/png' },
              { src: artworkUrl, sizes: '384x384', type: 'image/png' },
              { src: artworkUrl, sizes: '512x512', type: 'image/png' },
          ]
        });
    } catch(e) {
        console.warn("Metadata update failed", e);
    }
  }, []);

  // ★重要: Wake Lock管理 (バックグラウンド維持)
  const toggleWakeLock = async (shouldLock) => {
    if ('wakeLock' in navigator) {
        try {
            if (shouldLock && !wakeLockRef.current) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            } else if (!shouldLock && wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (err) {
            console.log("Wake Lock error:", err);
        }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Start Animation Loop Helper
    const startAnimationLoop = () => {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(animateProgress);
    };

    const stopAnimationLoop = () => {
        cancelAnimationFrame(rafRef.current);
    };

    const handleAudioPlay = () => {
      // ★ガード: クールダウン中は再生イベントを無視する（切断時の誤作動防止）
      if (pauseCooldownRef.current) {
          console.log("Play ignored due to cooldown");
          audio.pause(); // 強制停止
          return;
      }

      setIsPlaying(true);
      setErrorMsg(null);
      startAnimationLoop();
      toggleWakeLock(true); // スリープ防止ON

      if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
          updateMetadata(); 
          syncPositionState(audio); 
      }
    };

    const handleAudioPause = () => {
      if (audio.ended) return; 

      // ★即時反映: 状態をすぐに更新
      isPlayingRef.current = false;
      setIsPlaying(false);
      stopAnimationLoop();
      toggleWakeLock(false); // スリープ防止OFF

      // ★重要: 一時停止直後は「誤再生ガード」を有効にする (1秒間)
      // これにより、Bluetooth切断時の出力切り替えによる勝手な再生を防ぐ
      pauseCooldownRef.current = true;
      setTimeout(() => {
          pauseCooldownRef.current = false;
      }, 1000);

      if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
          syncPositionState(audio); 
      }
    };

    const handleAudioEnded = () => {
      playNext();
    };

    const handleTimeUpdate = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        if (Math.floor(audio.currentTime) % 2 === 0) {
            syncPositionState(audio);
        }
      }
    };

    const handleSeeked = () => {
        if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
        syncPositionState(audio);
    };

    const handleLoadedMetadata = () => {
        if (audio.duration) {
            syncPositionState(audio);
            if (audio.currentTime === 0) setProgress(0);
        }
    };

    const handleAudioError = (e) => {
        console.warn("Audio error:", e);
        setIsPlaying(false);
        stopAnimationLoop();
        toggleWakeLock(false);
        if (audio.error) {
            if (audio.error.code === 4) { 
                setErrorMsg(`Failed to load "${currentTrackRef.current?.title}". Source not found.`);
            } else if (audio.error.code === 3) { 
                setErrorMsg("Playback error: decode failed.");
            } else {
                console.warn("Unknown audio error occurred.");
            }
        }
    };

    // Attach listeners
    audio.addEventListener('play', handleAudioPlay);
    audio.addEventListener('pause', handleAudioPause);
    audio.addEventListener('ended', handleAudioEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('seeked', handleSeeked);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleAudioError);

    if ('mediaSession' in navigator) {
      const actions = [
        ['play', () => {
            console.log("BT: Play");
            audioRef.current.play().catch(e => console.warn("BT Play failed", e));
        }],
        ['pause', () => {
            console.log("BT: Pause");
            audioRef.current.pause();
        }],
        ['stop', () => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }],
        ['previoustrack', () => playPrev()],
        ['nexttrack', () => playNext()],
        ['seekto', (details) => {
            if (details.seekTime && isFinite(details.seekTime)) {
                audioRef.current.currentTime = details.seekTime;
                syncPositionState(audioRef.current);
                if (audioRef.current.duration) setProgress((details.seekTime / audioRef.current.duration) * 100);
            }
        }]
      ];

      actions.forEach(([action, handler]) => {
        try { navigator.mediaSession.setActionHandler(action, handler); } catch (e) {}
      });
    }

    return () => {
      audio.removeEventListener('play', handleAudioPlay);
      audio.removeEventListener('pause', handleAudioPause);
      audio.removeEventListener('ended', handleAudioEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('seeked', handleSeeked);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleAudioError);
      stopAnimationLoop();
    };
  }, [playNext, playPrev, updateMetadata, animateProgress]); 

  // --- 5. Track Loading & Metadata Update ---
  useEffect(() => {
    if (!currentTrack) return;
    const audio = audioRef.current;
    
    updateMetadata();

    const safeUrl = currentTrack.url ? encodeURI(currentTrack.url) : '';

    if (safeUrl && audio.src !== safeUrl) {
        // ★修正: 再生中かどうかの判定を Ref で行う（Stateの遅延回避）
        const wasPlaying = !audio.paused || isPlayingRef.current; 
        
        audio.src = safeUrl;
        audio.title = currentTrack.title;
        audio.preload = "auto"; 
        
        setErrorMsg(null);
        setProgress(0); 
        audio.load();

        // ★ガード: クールダウン中（切断直後など）なら自動再生しない
        if (wasPlaying && !pauseCooldownRef.current) { 
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn("Autoplay prevented:", error);
                    setIsPlaying(false);
                });
            }
        } else {
            // 停止状態へ
            setIsPlaying(false);
            if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'paused';
                setTimeout(() => syncPositionState(audio), 200); 
            }
        }
    }
  }, [currentTrack, updateMetadata]); 

  // --- Volume Sync ---
  useEffect(() => {
    if(audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // --- Playlist Filter Sync ---
  useEffect(() => {
    if (activePlaylist === 'All') setFilteredTracks(tracks);
    else setFilteredTracks(tracks.filter(t => t.genre === activePlaylist));
  }, [tracks, activePlaylist]);

  // --- Scroll to Active Track ---
  useEffect(() => {
    if (currentTrack && trackRefs.current[currentTrack.id] && scrollContainerRef.current) {
      const element = trackRefs.current[currentTrack.id];
      const container = scrollContainerRef.current;
      const scrollToCenter = () => {
        const target = element.offsetLeft + (element.offsetWidth / 2) - (container.clientWidth / 2);
        container.scrollTo({ left: target, behavior: 'smooth' });
      };
      requestAnimationFrame(() => {
          scrollToCenter();
          setTimeout(scrollToCenter, 400);
      });
    }
  }, [currentTrack, filteredTracks]);

  // Mouse effect
  useEffect(() => {
    let ticking = false;
    const handleMove = (e) => {
      if(!ticking) {
        window.requestAnimationFrame(() => {
          setMousePos({ x: (e.clientX / window.innerWidth - 0.5) * 30, y: (e.clientY / window.innerHeight - 0.5) * 30 });
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  const currentPlaylistStyle = playlists.find(p => p.id === activePlaylist) || playlists[0];
  const isAnonymous = !user || user.isAnonymous;
  const isAdmin = user && !user.isAnonymous && user.email === ADMIN_EMAIL; 

  return (
    <div 
        className="flex bg-[#020202] text-white font-sans overflow-hidden transition-colors duration-1000 select-none relative"
        style={{ height: 'var(--app-height, 100vh)' }}
    >
        
      {/* Upload/Login Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-3xl p-6 w-full max-w-lg shadow-2xl animate-slide-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {isAnonymous ? <><Lock size={20}/> Admin Login</> : <><Upload size={20}/> Add New Track</>}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-neutral-400 hover:text-white"><X/></button>
            </div>
              
            {isAnonymous ? (
              <div className="space-y-6 text-center py-4">
                  <p className="text-sm text-neutral-400">Please login with your authorized Google account to manage music.</p>
                  
                  <button 
                    onClick={handleGoogleLogin}
                    className="w-full bg-white text-black font-bold py-4 rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                  >
                     <Chrome size={20} className="text-blue-500" />
                     Sign in with Google
                  </button>
                  
                  {authError && <p className="text-red-400 text-xs mt-2">{authError}</p>}
              </div>
            ) : (
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="flex justify-between items-end mb-4">
                   <div className="flex flex-col">
                     <span className={`text-xs font-bold flex items-center gap-1 ${isAdmin ? 'text-green-500' : 'text-red-400'}`}>
                        {isAdmin ? <Lock size={12}/> : <AlertTriangle size={12}/>} 
                        {isAdmin ? 'Authorized Admin' : 'Unauthorized Account'}
                     </span>
                     <span className="text-[10px] text-neutral-500">{user.email}</span>
                   </div>
                   <button type="button" onClick={() => firebaseRefs.current.auth.signOut()} className="text-xs text-neutral-500 hover:text-white underline">Sign out</button>
                </div>

                {!isAdmin && (
                    <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg text-xs text-red-300 mb-4">
                        This Google account is not authorized to upload tracks. Please sign out and use the admin account.
                    </div>
                )}

                {isAdmin && (
                <>
                    <div>
                    <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Title</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors" placeholder="Song Title" value={newTrackData.title} onChange={e => setNewTrackData({...newTrackData, title: e.target.value})} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Artist</label>
                        <input type="text" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors" placeholder="Artist Name" value={newTrackData.artist} onChange={e => setNewTrackData({...newTrackData, artist: e.target.value})} required />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Genre</label>
                        <select className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors appearance-none" value={newTrackData.genre} onChange={e => setNewTrackData({...newTrackData, genre: e.target.value})}>
                        {playlists.filter(p => p.id !== 'All').map(p => (<option key={p.id} value={p.id} className="bg-neutral-900">{p.label}</option>))}
                        </select>
                    </div>
                    </div>
                    <div>
                    <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Audio URL (mp3)</label>
                    <input type="url" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors" placeholder="https://example.com/song.mp3" value={newTrackData.audioUrl} onChange={e => setNewTrackData({...newTrackData, audioUrl: e.target.value})} required />
                    </div>
                    <div>
                    <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Cover Image URL</label>
                    <input type="url" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors" placeholder="https://example.com/cover.jpg" value={newTrackData.coverUrl} onChange={e => setNewTrackData({...newTrackData, coverUrl: e.target.value})} />
                    </div>
                    <div>
                    <label className="block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1">Order (Sort)</label>
                    <input type="number" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-white/30 transition-colors" placeholder="0" value={newTrackData.order} onChange={e => setNewTrackData({...newTrackData, order: e.target.value})} />
                    </div>
                    <button type="submit" disabled={uploading} className="w-full bg-white text-black font-bold py-4 rounded-xl mt-4 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100">{uploading ? 'Adding...' : 'Save Track'}</button>
                </>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[200] w-[90%] max-w-md animate-slide-in" role="alert">
            <div className="bg-red-500/10 border border-red-500/50 backdrop-blur-xl p-4 rounded-xl flex items-start gap-3 shadow-2xl">
                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex flex-col gap-1">
                    <h3 className="font-bold text-red-500 text-sm">Error</h3>
                    <p className="text-xs text-red-200 leading-relaxed">{errorMsg}</p>
                </div>
                <button onClick={() => setErrorMsg(null)} className="ml-auto text-red-400 hover:text-white" aria-label="Close error message">
                  <X size={18} aria-hidden="true" />
                </button>
            </div>
        </div>
      )}

      <div 
        className="fixed inset-0 blur-[150px] opacity-20 pointer-events-none transition-all duration-1000" 
        style={{ 
          background: `radial-gradient(circle at ${50 + mousePos.x}% ${50 + mousePos.y}%, ${currentPlaylistStyle.glow}, transparent 70%)`,
          transform: 'translateZ(0)'
        }}
        aria-hidden="true"
      />

      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] lg:hidden" 
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 w-72 bg-black/80 lg:bg-black/40 backdrop-blur-3xl p-8 flex flex-col gap-10 border-r border-white/5 z-[110] transition-transform duration-500 overflow-y-auto
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} aria-label="Sidebar">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 bg-gradient-to-tr ${currentPlaylistStyle.color} rounded-2xl flex items-center justify-center shadow-2xl transition-all duration-1000 ring-2 ring-white/10`} aria-hidden="true">
              <Sparkles size={28} className="text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-black text-2xl tracking-tight leading-none uppercase">AKIKO</span>
              <span className="text-[10px] text-neutral-500 font-bold tracking-widest uppercase mt-1">Music Player</span>
            </div>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-neutral-400 hover:text-white" aria-label="Close sidebar">
            <X size={24} aria-hidden="true" />
          </button>
        </div>
          
        <nav className="flex flex-col gap-8 flex-1" aria-label="Main Navigation">
          <div className="space-y-4">
            <h2 className="text-[10px] font-black text-neutral-600 uppercase tracking-[0.2em] px-4">Collections</h2>
            <div className="space-y-1">
              {playlists.map(p => (
                <button 
                  key={p.id}
                  onClick={() => {
                    setActivePlaylist(p.id);
                    setIsSidebarOpen(false);
                  }}
                  aria-current={activePlaylist === p.id ? 'page' : undefined}
                  className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 ${activePlaylist === p.id ? 'bg-white/10 text-white shadow-lg' : 'text-neutral-500 hover:text-white hover:bg-white/5'}`}
                >
                  <span className={activePlaylist === p.id ? 'text-white' : 'text-neutral-600'}>{p.icon}</span>
                  <span className="text-[11px] font-black uppercase tracking-wider">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-[10px] font-black text-neutral-600 uppercase tracking-[0.2em] px-4">Admin Tools</h2>
            <div className="space-y-1">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-neutral-500 hover:text-white hover:bg-white/5 transition-all duration-300"
                  aria-label="Add Track"
                >
                  <span className="text-neutral-600 group-hover:text-white">
                    {isAnonymous ? <Lock size={20}/> : <PlusSquare size={20}/>}
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-wider">
                    {isAnonymous ? 'Admin Login' : 'Add Track'}
                  </span>
                </button>
                  
                <button 
                  onClick={exportTracks}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-neutral-500 hover:text-white hover:bg-white/5 transition-all duration-300"
                  aria-label="Export Tracks"
                >
                  <span className="text-neutral-600 group-hover:text-white"><Download size={20}/></span>
                  <span className="text-[11px] font-black uppercase tracking-wider">Export Tracks JSON</span>
                </button>

                <button 
                  onClick={generateSitemap}
                  disabled={generatingSitemap || !user}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-neutral-500 hover:text-white hover:bg-white/5 transition-all duration-300 disabled:opacity-50"
                  aria-label="Generate Sitemap"
                >
                  <span className="text-neutral-600 group-hover:text-white">
                    {generatingSitemap ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/> : <FileText size={20} />}
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-wider">
                    {generatingSitemap ? 'Generating...' : 'Download Sitemap'}
                  </span>
                </button>
            </div>
          </div>

          {/* New App Download Section */}
          <div className="space-y-4">
            <h2 className="text-[10px] font-black text-neutral-600 uppercase tracking-[0.2em] px-4">Mobile App</h2>
            <div className="space-y-1">
                <a 
                  href="https://akiko.cosu.jp/music/app-release.apk"
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-neutral-500 hover:text-white hover:bg-white/5 transition-all duration-300 group"
                  aria-label="Download Android App"
                >
                  <span className="text-neutral-600 group-hover:text-white">
                    <Smartphone size={20} />
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-wider">
                    Android App (.apk)
                  </span>
                </a>
            </div>
          </div>

          <div className="mt-auto pt-6 border-t border-white/5">
            <p className="text-[10px] text-neutral-600 text-center leading-relaxed">
              &copy; 2025 AKIKO Music Player.<br/>All Rights Reserved.
            </p>
          </div>
        </nav>
      </aside>

      <main className="flex-1 flex flex-col relative z-10 overflow-hidden">
          
        <header className="px-6 lg:px-12 pt-6 lg:pt-8 flex items-end justify-between shrink-0">
          <div className="flex items-center gap-6 animate-slide-in">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-3 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={24} aria-hidden="true" />
            </button>
            <div>
              <h1 className="text-2xl lg:text-5xl font-black tracking-tighter leading-none mb-2 truncate max-w-[200px] lg:max-w-none">
                Akiko
              </h1>
              <div className={`h-1 w-10 lg:w-16 bg-gradient-to-r ${currentPlaylistStyle.color} rounded-full shadow-lg shadow-white/5`} aria-hidden="true" />
            </div>
          </div>

          <div className="flex flex-col items-end gap-1.5 pb-1">
             <div className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-full border border-white/5 backdrop-blur-md" role="status" aria-label={user ? "System Online" : "System Offline"}>
                <div className={`w-1.5 h-1.5 rounded-full ${user ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-500'} animate-pulse`} aria-hidden="true" />
                <span className={`text-[8px] font-black uppercase tracking-[0.2em] ${user ? 'text-green-500' : 'text-red-500'}`}>
                  {user ? 'SYSTEM ONLINE' : 'OFFLINE'}
                </span>
             </div>
             <p className="text-[10px] font-black uppercase tracking-[0.4em] text-neutral-500 hidden sm:block text-right">
               {filteredTracks.length} TRACKS
             </p>
          </div>
        </header>

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none -z-10 overflow-hidden" aria-hidden="true">
          <span className="text-[20vw] lg:text-[25vw] font-black opacity-[0.03] uppercase tracking-tighter transition-all duration-1000 whitespace-nowrap">
            {currentTrack?.artist || "AKIKO"}
          </span>
        </div>

        <div className="flex-1 flex items-center relative min-h-0">
          <div 
            ref={scrollContainerRef}
            className="flex items-center gap-8 lg:gap-12 px-[30vw] lg:px-[35vw] overflow-x-auto no-scrollbar py-8 snap-x snap-mandatory h-full"
            style={{ 
              scrollBehavior: 'smooth',
              scrollPadding: '0 30vw'
            }}
            role="list"
            aria-label="Track list"
          >
            {loading ? (
              <div className="flex flex-col items-center gap-4 mx-auto">
                <div className="w-16 h-16 border-t-2 border-white rounded-full animate-spin" aria-hidden="true" />
                <p className="text-xs font-bold text-neutral-500 tracking-widest uppercase">Loading...</p>
              </div>
            ) : filteredTracks.length === 0 ? (
               <div className="flex flex-col items-center gap-4 mx-auto opacity-50">
                <Disc size={48} className="text-neutral-500" aria-hidden="true" />
                <p className="text-xs font-bold text-neutral-500 tracking-widest uppercase">No tracks found</p>
              </div>
            ) : (
              filteredTracks.map(track => {
                const isActive = currentTrack?.id === track.id;
                return (
                  <div 
                    key={track.id}
                    ref={el => trackRefs.current[track.id] = el}
                    onClick={() => handleTrackClick(track)}
                    style={{ scrollSnapAlign: 'center' }}
                    role="listitem"
                    className={`relative flex-shrink-0 transition-all duration-700 ease-out snap-center cursor-pointer
                      ${isActive ? 'w-[260px] lg:w-[320px] scale-100 opacity-100 z-10' : 'w-[160px] lg:w-[220px] scale-80 opacity-30 hover:opacity-60 hover:scale-85'}
                    `}
                  >
                    <div className={`relative aspect-square rounded-[2rem] lg:rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-700 ${isActive ? 'ring-4 ring-white/20 shadow-[0_40px_80px_rgba(0,0,0,0.7)]' : 'ring-1 ring-white/5'}`}>
                      {track.cover ? (
                        <img 
                            src={track.cover} 
                            className="w-full h-full object-cover" 
                            alt={`Album art for ${track.title} by ${track.artist}`}
                            loading={isActive ? "eager" : "lazy"}
                            fetchpriority={isActive ? "high" : "low"}
                            onError={(e) => { e.target.src = "https://via.placeholder.com/512?text=No+Image"; }}
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-900 flex items-center justify-center"><Music size={48} className="text-neutral-800" aria-hidden="true" /></div>
                      )}
                        
                      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent flex flex-col justify-end p-6 lg:p-8">
                        <p className={`text-[10px] font-black uppercase tracking-[0.3em] mb-2 transition-all duration-700 ${isActive ? 'text-white' : 'text-white/60'}`}>
                          {track.artist}
                        </p>
                        <h2 className={`font-black tracking-tight leading-tight transition-all duration-700 truncate ${isActive ? 'text-xl lg:text-2xl' : 'text-base lg:text-lg'}`}>
                          {track.title}
                        </h2>
                          
                        {isActive && isPlaying && (
                          <div className="mt-4 flex gap-1 h-4 items-end" aria-hidden="true">
                            {[1, 2, 3, 4].map(i => (
                              <div key={i} className={`w-1 bg-white rounded-full animate-fake-bounce`} style={{ animationDelay: `${i * 0.15}s`, height: '100%' }} />
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={`absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-300 ${isActive ? '' : 'hover:opacity-100'}`}>
                        <div className="w-14 h-14 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20">
                          {isActive && isPlaying ? <Pause fill="white" size={20} aria-hidden="true" /> : <Play fill="white" size={20} className="ml-1" aria-hidden="true" />}
                        </div>
                      </div>
                    </div>

                    {isActive && (
                       <div className={`absolute -inset-8 bg-gradient-to-tr ${currentPlaylistStyle.color} blur-[80px] opacity-20 -z-10 animate-pulse-slow`} aria-hidden="true" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          <button 
            onClick={handlePrev}
            className="absolute left-4 lg:left-10 w-12 h-12 lg:w-14 lg:h-14 bg-white/5 hover:bg-white/10 backdrop-blur-2xl border border-white/10 rounded-full hidden lg:flex items-center justify-center transition-all hover:scale-110 active:scale-95 z-20"
            aria-label="Previous track"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <button 
            onClick={handleNext}
            className="absolute right-4 lg:right-10 w-12 h-12 lg:w-14 lg:h-14 bg-white/5 hover:bg-white/10 backdrop-blur-2xl border border-white/10 rounded-full hidden lg:flex items-center justify-center transition-all hover:scale-110 active:scale-95 z-20"
            aria-label="Next track"
          >
            <ChevronRight size={24} aria-hidden="true" />
          </button>
        </div>

        <footer className="h-20 lg:h-28 px-4 lg:px-10 flex items-center justify-between bg-black/60 lg:bg-black/40 backdrop-blur-3xl mx-2 lg:mx-8 mb-4 lg:mb-8 rounded-[1.5rem] lg:rounded-[2.5rem] border border-white/10 shadow-2xl shrink-0 z-50">
          <div className="w-[30%] lg:w-1/3 flex items-center gap-3">
             {currentTrack && (
               <>
                 <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden hidden sm:block ring-1 ring-white/10 flex-shrink-0 animate-slide-in">
                    <img src={currentTrack.cover} alt={`${currentTrack.title} cover`} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.src = "https://via.placeholder.com/512?text=No+Image"; }} />
                 </div>
                 <div className="flex flex-col min-w-0 animate-slide-in">
                    <span className="text-[8px] font-black uppercase tracking-[0.2em] text-neutral-500 mb-0.5 truncate">NOW PLAYING</span>
                    <span className="text-xs lg:text-lg font-black truncate text-white leading-none">{currentTrack.title}</span>
                 </div>
               </>
             )}
          </div>

          <div className="flex flex-col items-center gap-1 lg:gap-3 w-[40%] lg:w-1/3">
             <div className="flex items-center gap-4 lg:gap-8">
                <button onClick={handlePrev} className="text-neutral-500 hover:text-white active:scale-90 transition-transform" aria-label="Previous track">
                  <SkipBack fill="currentColor" className="w-4 h-4 lg:w-[18px] lg:h-[18px]" aria-hidden="true" />
                </button>
                <button 
                  onClick={() => {
                      // Toggle play via direct Audio element to keep logic consistent
                      if (isPlaying) audioRef.current.pause();
                      else audioRef.current.play().catch(console.warn);
                  }} 
                  className={`w-10 h-10 lg:w-12 lg:h-12 bg-white text-black rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-90 shadow-xl shadow-white/10`}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <Pause fill="black" className="w-[18px] h-[18px] lg:w-5 lg:h-5" aria-hidden="true" /> : <Play fill="black" className="w-[18px] h-[18px] lg:w-5 lg:h-5 ml-0.5" aria-hidden="true" />}
                </button>
                <button onClick={handleNext} className="text-neutral-500 hover:text-white active:scale-90 transition-transform" aria-label="Next track">
                  <SkipForward fill="currentColor" className="w-4 h-4 lg:w-[18px] lg:h-[18px]" aria-hidden="true" />
                </button>
             </div>
             
             <div className="w-full max-w-[200px] lg:max-w-xs flex items-center gap-2 lg:gap-3">
                <span className="text-[8px] font-black text-neutral-600 tabular-nums">
                  {Math.floor(audioRef.current.currentTime / 60)}:{String(Math.floor(audioRef.current.currentTime % 60)).padStart(2, '0')}
                </span>
                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                   {/* Removed transition for smooth 60fps update */}
                   <div className={`h-full bg-gradient-to-r ${currentPlaylistStyle.color}`} style={{ width: `${progress}%` }} />
                </div>
                <span className="text-[8px] font-black text-neutral-600 tabular-nums">
                  {audioRef.current.duration ? `${Math.floor(audioRef.current.duration / 60)}:${String(Math.floor(audioRef.current.duration % 60)).padStart(2, '0')}` : '0:00'}
                </span>
             </div>
          </div>

          <div className="w-[30%] lg:w-1/3 flex justify-end items-center">
             <div className="hidden md:flex items-center gap-3">
                <Volume2 size={16} className="text-neutral-600" aria-hidden="true" />
                <div className="w-16 lg:w-20 h-1 bg-white/5 rounded-full relative group overflow-hidden">
                   <input 
                    type="range" min="0" max="1" step="0.01" value={volume} 
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    aria-label="Volume control"
                   />
                   <div className="h-full bg-white/30 group-hover:bg-white transition-colors" style={{ width: `${volume * 100}%` }} />
                </div>
             </div>
             <div className="md:hidden text-neutral-500">
               <Volume2 size={16} aria-hidden="true" />
             </div>
          </div>
        </footer>

      </main>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        
        @keyframes fake-bounce {
          0%, 100% { height: 4px; }
          50% { height: 100%; }
        }
        .animate-fake-bounce { animation: fake-bounce 0.8s ease-in-out infinite; }
        
        @keyframes slide-in {
          from { transform: translateY(15px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-in { animation: slide-in 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.1; }
          50% { transform: scale(1.1); opacity: 0.25; }
        }
        .animate-pulse-slow { animation: pulse-slow 5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
